/**
 * Export logic — download/copy as .drawio, .svg, .png.
 */

import { store } from './state';
import { getExportFormat } from './controls';

let copyBtn: HTMLButtonElement;
let downloadBtn: HTMLButtonElement;

export function initExport(): void {
  copyBtn = document.getElementById('copy-btn') as HTMLButtonElement;
  downloadBtn = document.getElementById('download-btn') as HTMLButtonElement;

  if (copyBtn) copyBtn.addEventListener('click', onCopy);
  if (downloadBtn) downloadBtn.addEventListener('click', onDownload);
}

async function onCopy(): Promise<void> {
  const state = store.get();
  const format = getExportFormat();

  if (format === 'drawio' || format === 'mermaid') {
    await navigator.clipboard.writeText(state.output);
    showToast('Copied to clipboard');
  } else if (format === 'svg') {
    const svg = getSvgContent();
    if (svg) {
      await navigator.clipboard.writeText(svg);
      showToast('SVG copied to clipboard');
    }
  } else if (format === 'png') {
    const blob = await getPngBlob();
    if (blob) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      showToast('PNG copied to clipboard');
    }
  }
}

function onDownload(): void {
  const state = store.get();
  const format = getExportFormat();

  if (format === 'drawio') {
    downloadFile(state.output, 'diagram.drawio', 'application/xml');
  } else if (format === 'svg') {
    const svg = getSvgContent();
    if (svg) downloadFile(svg, 'diagram.svg', 'image/svg+xml');
  } else if (format === 'png') {
    getPngBlob().then((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'diagram.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  } else if (format === 'mermaid') {
    downloadFile(state.output, 'diagram.mmd', 'text/plain');
  }
}

function getSvgContent(): string | null {
  const preview = document.getElementById('diagram-preview');
  const svg = preview?.querySelector('svg');
  if (!svg) return null;

  const clone = svg.cloneNode(true) as SVGSVGElement;

  // Ensure xmlns for standalone SVG
  if (!clone.getAttribute('xmlns')) {
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  // Ensure explicit width/height from viewBox for Image rendering
  const vb = clone.getAttribute('viewBox');
  if (vb && (!clone.getAttribute('width') || !clone.getAttribute('height'))) {
    const parts = vb.split(/[\s,]+/);
    clone.setAttribute('width', parts[2]);
    clone.setAttribute('height', parts[3]);
  }

  return clone.outerHTML;
}

async function getPngBlob(): Promise<Blob | null> {
  const svgStr = getSvgContent();
  if (!svgStr) return null;

  return new Promise((resolve) => {
    const img = new Image();
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = 2; // Retina
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        resolve(b);
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    img.src = url;
  });
}

function downloadFile(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function showToast(message: string): void {
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => toast.classList.add('show'), 10);
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => document.body.removeChild(toast), 300);
  }, 2000);
}
